import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { MAYA_LOGO_TRANSPARENT } from "../components/MayaLogo";
import {
  GraduationCap,
  Sparkles,
  ClipboardCheck,
  Check,
  Star,
  Play,
  Plus,
  Minus,
  Quote,
  BookOpen,
  TrendingUp,
  Mic,
  Zap,
  BadgeCheck,
  ArrowRight,
  ShieldCheck,
  Users,
  Timer,
  Award,
} from "lucide-react";

/* ==================================================================
   Shared hooks
   ================================================================== */

/** Scroll-triggered reveal: adds `inView=true` when element enters viewport. */
const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(node);
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -80px 0px", ...options }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

/** RAF-based count-up from 0 → target when `inView` becomes true. */
const useCountUp = (target, duration = 1600, inView = false) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVal(target);
      return;
    }
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return val;
};

/**
 * Mouse-parallax hook — writes normalized cursor coords (-1..1) to `--mx`/`--my`
 * CSS variables on the container. Fine-pointer only; respects reduced motion.
 * Children can consume via `calc(var(--mx, 0) * Npx)` etc. Container/mockups
 * do NOT move — only children that opt in via CSS vars do.
 */
const useMouseParallax = () => {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!finePointer || reduced) return;
    let raf = 0;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    const tick = () => {
      currentX += (targetX - currentX) * 0.12;
      currentY += (targetY - currentY) * 0.12;
      node.style.setProperty("--mx", currentX.toFixed(3));
      node.style.setProperty("--my", currentY.toFixed(3));
      if (Math.abs(targetX - currentX) > 0.001 || Math.abs(targetY - currentY) > 0.001) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = 0;
      }
    };
    const start = () => { if (!raf) raf = requestAnimationFrame(tick); };
    const onMove = (e) => {
      const rect = node.getBoundingClientRect();
      targetX = ((e.clientX - rect.left) / rect.width) * 2 - 1; // -1..1
      targetY = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      start();
    };
    const onLeave = () => { targetX = 0; targetY = 0; start(); };
    node.addEventListener("mousemove", onMove);
    node.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      node.removeEventListener("mousemove", onMove);
      node.removeEventListener("mouseleave", onLeave);
    };
  }, []);
  return ref;
};

/* ==================================================================
   HERO
   ================================================================== */

const HERO_ILLUSTRATION =
  "https://customer-assets.emergentagent.com/job_medily-pro/artifacts/k9vb3cu9_Test%201.webp";

const AssessingCard = () => (
  <div
    className="hidden md:flex absolute top-6 right-[-14px] items-center gap-3 bg-white/95 backdrop-blur border border-white rounded-2xl px-5 py-4 shadow-[0_35px_75px_-15px_rgba(29,78,216,0.4)] medily-float"
    data-testid="hero-assessing-card"
  >
    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#1d4ed8] to-[#3b6ff5] flex items-center justify-center p-2 shadow-[0_10px_25px_-8px_rgba(29,78,216,0.6)]">
      <img
        src={MAYA_LOGO_TRANSPARENT}
        alt="Maya"
        className="w-full h-auto"
        style={{ filter: "brightness(0) invert(1)" }}
        draggable="false"
      />
    </div>
    <div>
      <p className="text-[13.5px] font-semibold text-slate-900">Assessing your answer…</p>
      <div className="mt-1.5 flex items-end gap-[3px] h-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <span
            key={i}
            className="w-[3.5px] h-full rounded-full bg-gradient-to-t from-[#1d4ed8] to-[#7aa2ff] medily-pulse-bar"
            style={{ animationDelay: `${i * 90}ms` }}
          />
        ))}
      </div>
    </div>
  </div>
);

const SessionScoreCard = () => (
  <div
    className="hidden md:block absolute bottom-8 left-[-14px] bg-white/95 backdrop-blur border border-white rounded-2xl p-4 w-[230px] shadow-[0_35px_75px_-15px_rgba(29,78,216,0.4)] medily-float-slow"
    data-testid="hero-score-card"
  >
    <div className="flex items-center justify-between">
      <p className="text-[12px] text-slate-500">Session score</p>
      <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#e6f7ee] text-[#15803d] font-medium">+12%</span>
    </div>
    <p className="mt-1 text-[28px] font-semibold text-slate-900 tracking-tight">
      8.4 <span className="text-[13px] text-slate-400 font-normal">/ 10</span>
    </p>
    <div className="mt-2 flex items-center gap-1">
      {[85, 92, 74, 88, 78].map((v, i) => (
        <div key={i} className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#1d4ed8] to-[#4f7cff]" style={{ width: `${v}%` }} />
        </div>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-slate-500">Communication · Empathy · Ethics</p>
  </div>
);

const HERO_TRUST = [
  { icon: GraduationCap, title: "University-Specific", sub: "Preparation" },
  { icon: BookOpen, title: "Expert-Written", sub: "Resources" },
  { icon: ClipboardCheck, title: "Realistic MMI", sub: "Practice" },
];

const Hero = () => {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] bg-[#bcd3ff] rounded-full blur-3xl opacity-90" />
        <div className="absolute top-32 -right-40 w-[640px] h-[640px] bg-[#d5e4ff] rounded-full blur-3xl opacity-95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.6),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-14 pb-28 md:pb-36 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#dbe6ff] text-[#1d4ed8] text-[13px] font-medium shadow-sm">
            <BadgeCheck className="w-4 h-4" />
            Your Complete MMI Preparation Platform
          </div>
          <h1
            className="mt-6 font-semibold text-slate-900 leading-[1.02] tracking-tight text-[46px] md:text-[62px]"
            data-testid="hero-heading"
          >
            The UK’s leading{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#1d4ed8]">University-Specific</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
            </span>{" "}
            MMI specialists.
          </h1>
          <p className="mt-6 text-slate-500 text-[17px] leading-relaxed max-w-[560px]">
            Build your knowledge with expert-written guides, practise university-specific MMI
            stations with Maya and prepare for the demands of your chosen UK medical school.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => navigate("/pricing")}
              className="px-7 py-3.5 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_15px_35px_-10px_rgba(29,78,216,0.6)]"
              data-testid="hero-get-started-btn"
            >
              Get Started
            </button>
            <button
              onClick={() => navigate("/what-we-offer/maya")}
              className="px-7 py-3.5 rounded-full bg-white border border-slate-200 text-slate-800 font-medium hover:border-[#1d4ed8] hover:text-[#1d4ed8] transition shadow-sm inline-flex items-center gap-2"
              data-testid="hero-meet-maya-btn"
            >
              <Play className="w-4 h-4" /> Meet Maya
            </button>
          </div>

          {/* Social proof */}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex -space-x-2">
              {[47, 25, 52, 12, 31].map((i) => (
                <img key={i} src={`https://i.pravatar.cc/60?img=${i}`} alt="" className="w-9 h-9 rounded-full border-2 border-white" />
              ))}
            </div>
            <div className="flex items-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-amber-400" />
              ))}
              <span className="text-slate-800 text-[13px] font-semibold ml-1">4.9 / 5</span>
            </div>
          </div>

          {/* Trust cards */}
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-[560px]">
            {HERO_TRUST.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-2xl bg-white/75 backdrop-blur border border-white p-3 shadow-[0_15px_35px_-15px_rgba(29,78,216,0.5)] hover:shadow-[0_20px_45px_-15px_rgba(29,78,216,0.6)] transition"
              >
                <div className="w-10 h-10 rounded-xl bg-[#eaf1ff] flex items-center justify-center text-[#1d4ed8]">
                  <f.icon className="w-[18px] h-[18px]" />
                </div>
                <div className="leading-tight">
                  <p className="text-[13px] font-semibold text-slate-800">{f.title}</p>
                  <p className="text-[12px] text-slate-500">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right visual */}
        <div className="relative">
          <div className="rounded-[28px] overflow-hidden bg-gradient-to-br from-[#dbe6ff] via-[#eef4ff] to-white p-2 shadow-[0_55px_120px_-25px_rgba(29,78,216,0.55)]">
            <img
              src={HERO_ILLUSTRATION}
              alt="Painterly medical illustration"
              className="w-full h-[440px] md:h-[520px] object-cover rounded-[22px]"
              draggable="false"
            />
          </div>
          <AssessingCard />
          <SessionScoreCard />
        </div>
      </div>
    </section>
  );
};

/* ==================================================================
   STATISTICS BAR (with count-up)
   ================================================================== */

const STATS = [
  { value: 35, suffix: "+", label: "UK medical schools catered for" },
  { value: 75, suffix: "+", label: "Expert Resources" },
  { text: "24/7", label: "Access to Study Resources" },
  { value: 98, suffix: "%", label: "Would Recommend" },
];

const StatItem = ({ stat, inView }) => {
  const numeric = typeof stat.value === "number";
  const val = useCountUp(numeric ? stat.value : 0, 1600, inView);
  const display = numeric ? `${Math.round(val)}${stat.suffix || ""}` : stat.text;
  return (
    <div className="text-center md:text-left" data-testid={`stat-${stat.label.replace(/\s+/g, "-").toLowerCase()}`}>
      <p className="text-[36px] md:text-[44px] font-semibold tracking-tight tabular-nums">{display}</p>
      <p className="text-[13px] opacity-85">{stat.label}</p>
    </div>
  );
};

const StatsBand = () => {
  const { ref, inView } = useReveal();
  return (
    <section className="max-w-7xl mx-auto px-6" ref={ref} data-testid="stats-section">
      <div className="rounded-3xl bg-gradient-to-r from-[#1d4ed8] to-[#3b6ff5] p-8 md:p-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-white shadow-[0_45px_110px_-25px_rgba(29,78,216,0.75)]">
        {STATS.map((s, i) => (
          <StatItem key={i} stat={s} inView={inView} />
        ))}
      </div>
    </section>
  );
};

/* ==================================================================
   WHY CHOOSE US
   ================================================================== */

const WhyChooseUs = () => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 28px, 0)",
    opacity: inView ? 1 : 0,
    transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section
      ref={ref}
      className="max-w-4xl mx-auto px-6 pt-28 md:pt-36 pb-4 text-center"
      style={style}
      data-testid="why-choose-us-section"
    >
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">
        Why Choose Us?
      </span>
      <h2 className="mt-5 text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
        We{" "}
        <span className="relative inline-block">
          <span className="relative z-10 text-[#1d4ed8]">demystify</span>
          <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
        </span>{" "}
        the interview process.
      </h2>
      <p className="mt-5 text-slate-500 text-[17px] leading-relaxed">
        No scattered notes. No vague advice. No wondering what to practise next. Medily brings your
        learning, interview technique and realistic practice together in one clear platform.
      </p>
    </section>
  );
};

/* ==================================================================
   FOUR PILLARS
   ================================================================== */

const PILLARS = [
  {
    id: "learn",
    label: "PILLAR 01 · LEARN",
    icon: BookOpen,
    title: "Learn",
    description: "Build the knowledge behind strong answers by working through our focused guides covering:",
    bullets: [
      "Medical Ethics",
      "MMI Station Techniques",
      "Medical Case Studies",
      "NHS Hot Topics",
      "The NHS Constitution",
      "Interview Tips",
    ],
    phone: "/homepage/Learn 1 P.png",
    laptop: "/homepage/Learn 1 L.png",
    imageLeft: true,
    tone: "bg-white",
    floatingCards: [
      { pos: "top-4 -left-4 md:-left-8", icon: BookOpen, title: "75+ Expert Resources", tint: "from-white to-white" },
      { pos: "bottom-8 -right-4 md:-right-6", icon: Timer, title: "1–3 min topics", tint: "from-white to-white" },
      { pos: "top-1/2 -right-3 md:-right-8", icon: Check, title: "Progress saved", tint: "from-[#e6f7ee] to-white", accent: "text-[#15803d]" },
    ],
  },
  {
    id: "improve",
    label: "PILLAR 02 · IMPROVE",
    icon: TrendingUp,
    title: "Improve",
    description: "Understand what interviewers actually look for. Learn how to:",
    bullets: [
      "Structure answers clearly",
      "Buy time",
      "Recover when your mind goes blank",
      "Handle difficult questions",
    ],
    postBulletsNote: "And many more tips — all covered in our Interview Tips Guide.",
    phone: "/homepage/Improve 1 P.png",
    laptop: "/homepage/Improve 1 L.png",
    imageLeft: false,
    tone: "bg-gradient-to-b from-[#f5f8ff] via-white to-[#f7faff]",
    floatingCards: [
      { pos: "top-6 -right-4 md:-right-8", icon: ClipboardCheck, title: "Clear structure", tint: "from-white to-white" },
      { pos: "bottom-10 -left-4 md:-left-6", icon: Sparkles, title: "Confident delivery", tint: "from-white to-white" },
      { pos: "top-1/2 -left-3 md:-left-8", icon: Award, title: "Examiner insight", tint: "from-[#eef4ff] to-white", accent: "text-[#1d4ed8]" },
    ],
  },
  {
    id: "practise",
    label: "PILLAR 03 · PRACTISE",
    icon: Zap,
    title: "Practise",
    secondaryHeading: "Put your knowledge into action with Maya",
    description:
      "Complete university-specific MMI stations and receive structured feedback on your strengths and areas to improve. What’s more — Maya optimises your answer for you to see what a top-mark response looks like.",
    bullets: [],
    phone: "/homepage/Practise 1 P.png",
    laptop: "/homepage/Practise 1 L.png",
    imageLeft: true,
    tone: "bg-white",
    floatingCards: [
      { pos: "top-4 -left-4 md:-left-8", icon: Check, title: "Strengths", tint: "from-[#e6f7ee] to-white", accent: "text-[#15803d]" },
      { pos: "top-1/2 -right-4 md:-right-8", icon: TrendingUp, title: "Optimised Answer", tint: "from-[#eef4ff] to-white", accent: "text-[#1d4ed8]" },
      { pos: "bottom-8 -left-4 md:-left-6", icon: Award, title: "MMI Score 8.4/10", tint: "from-white to-white" },
    ],
  },
  {
    id: "experience",
    label: "PILLAR 04 · EXPERIENCE",
    icon: Users,
    title: "Experience",
    secondaryHeading: "Step into a live mock MMI",
    description:
      "Experience a realistic University-Specific Mock MMI built around your chosen medical school.",
    bullets: [
      "University-specific feedback",
      "12 realistic MMI stations",
      "Small groups of maximum 4 students per station",
      "Experienced interviewers",
    ],
    illustration: "/homepage/Picture1.png",
    imageLeft: false,
    tone: "bg-gradient-to-b from-[#f5f8ff] via-white to-white",
    floatingCards: [
      { pos: "top-4 -right-4 md:-right-6", icon: Users, title: "Live interviewers", tint: "from-white to-white" },
      { pos: "bottom-10 -left-4 md:-left-6", icon: Timer, title: "Realistic time pressure", tint: "from-[#eef4ff] to-white", accent: "text-[#1d4ed8]" },
    ],
  },
];

const FloatingCard = ({ card, delay, inView }) => {
  const Icon = card.icon;
  const outerStyle = {
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0, 24px, 0) scale(0.9)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
    willChange: "transform, opacity",
  };
  // Mouse-driven parallax + tilt (consumes --mx / --my from parent).
  // Falls back to 0 when the parent hasn't opted in (touch / reduced-motion).
  const tiltStyle = {
    transform:
      "translate3d(calc(var(--mx, 0) * 5px), calc(var(--my, 0) * 4px), 0)" +
      " rotateX(calc(var(--my, 0) * 2deg)) rotateY(calc(var(--mx, 0) * -2.5deg))",
    transition: "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform",
    transformStyle: "preserve-3d",
  };
  return (
    <div className={`absolute ${card.pos} z-10`} style={outerStyle}>
      <div className="medily-float-slow" style={{ willChange: "transform" }}>
        <div style={tiltStyle}>
          <div className={`flex items-center gap-2.5 rounded-2xl bg-gradient-to-br ${card.tint} border border-white px-3.5 py-2.5 shadow-[0_25px_55px_-15px_rgba(29,78,216,0.4)] backdrop-blur`}>
            <span className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm ${card.accent || "text-[#1d4ed8]"}`}>
              <Icon className="w-4 h-4" />
            </span>
            <span className="text-[12.5px] font-semibold text-slate-800 whitespace-nowrap">{card.title}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PillarVisual = ({ phone, laptop, alt, phoneOnLeft, inView, floatingCards = [] }) => {
  const parallaxRef = useMouseParallax();
  const laptopStyle = {
    transform: inView
      ? "translate3d(0,0,0) rotate(0deg) scale(1)"
      : `translate3d(${phoneOnLeft ? "-60px" : "60px"}, 24px, 0) rotate(${phoneOnLeft ? "-2deg" : "2deg"}) scale(0.97)`,
    opacity: inView ? 1 : 0,
    transition: "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 120ms, opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 120ms",
    willChange: "transform, opacity",
  };
  const phoneStyle = {
    transform: inView
      ? "translate3d(0,0,0) rotate(0deg) scale(1)"
      : `translate3d(${phoneOnLeft ? "-40px" : "40px"}, 32px, 0) rotate(${phoneOnLeft ? "3deg" : "-3deg"}) scale(0.94)`,
    opacity: inView ? 1 : 0,
    transition: "transform 1300ms cubic-bezier(0.22, 1, 0.36, 1) 320ms, opacity 1000ms cubic-bezier(0.22, 1, 0.36, 1) 320ms",
    willChange: "transform, opacity",
  };
  return (
    <div ref={parallaxRef} className="relative w-full" style={{ perspective: "1200px" }}>
      <div className="absolute -inset-8 -z-10 rounded-[52px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95" />
      <div className="absolute -inset-4 -z-10 rounded-[40px] bg-gradient-to-tr from-[#dbe6ff]/70 via-transparent to-[#eef4ff]/50 blur-2xl" />
      <div className="absolute inset-x-8 -bottom-4 -z-10 h-10 rounded-full bg-[#1d4ed8]/30 blur-2xl" />
      <img
        src={laptop}
        alt={`${alt} on laptop`}
        style={laptopStyle}
        className="w-full h-auto drop-shadow-[0_50px_80px_rgba(29,78,216,0.28)] select-none"
        data-testid={`pillar-${alt.toLowerCase()}-laptop-img`}
        draggable="false"
      />
      <img
        src={phone}
        alt={`${alt} on phone`}
        style={phoneStyle}
        className={`absolute -bottom-[2%] w-[36%] md:w-[32%] h-auto drop-shadow-[0_40px_75px_rgba(29,78,216,0.45)] select-none ${
          phoneOnLeft ? "left-[-3%] md:left-[-6%]" : "right-[-3%] md:right-[-6%]"
        }`}
        data-testid={`pillar-${alt.toLowerCase()}-phone-img`}
        draggable="false"
      />
      {floatingCards.map((c, i) => (
        <FloatingCard key={i} card={c} delay={480 + i * 140} inView={inView} />
      ))}
    </div>
  );
};

const IllustrationVisual = ({ src, alt, fromLeft, inView, floatingCards = [] }) => {
  const parallaxRef = useMouseParallax();
  const style = {
    transform: inView
      ? "translate3d(0,0,0) rotate(0deg) scale(1)"
      : `translate3d(${fromLeft ? "-60px" : "60px"}, 28px, 0) rotate(${fromLeft ? "-2deg" : "2deg"}) scale(0.96)`,
    opacity: inView ? 1 : 0,
    transition: "transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 180ms, opacity 1000ms cubic-bezier(0.22, 1, 0.36, 1) 180ms",
    willChange: "transform, opacity",
  };
  return (
    <div ref={parallaxRef} className="relative w-full" style={{ perspective: "1200px" }}>
      <div className="absolute -inset-10 -z-10 rounded-[64px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95" />
      <div
        style={style}
        className="rounded-[28px] overflow-hidden shadow-[0_55px_100px_-20px_rgba(29,78,216,0.45)] border border-white"
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-auto object-cover select-none block"
          data-testid={`pillar-${alt.toLowerCase()}-illustration-img`}
          draggable="false"
        />
      </div>
      {floatingCards.map((c, i) => (
        <FloatingCard key={i} card={c} delay={480 + i * 140} inView={inView} />
      ))}
    </div>
  );
};

const Pillar = ({ item }) => {
  const Icon = item.icon;
  const imgLeft = item.imageLeft;
  const { ref, inView } = useReveal();
  const copyStyle = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 32px, 0)",
    opacity: inView ? 1 : 0,
    transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section
      ref={ref}
      className={`relative w-full ${item.tone} py-24 md:py-32 overflow-hidden`}
      data-testid={`pillar-${item.id}-section`}
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className={`${imgLeft ? "lg:order-1" : "lg:order-2"} order-1`}>
            {item.illustration ? (
              <IllustrationVisual
                src={item.illustration}
                alt={item.title}
                fromLeft={imgLeft}
                inView={inView}
                floatingCards={item.floatingCards}
              />
            ) : (
              <PillarVisual
                phone={item.phone}
                laptop={item.laptop}
                alt={item.title}
                phoneOnLeft={imgLeft}
                inView={inView}
                floatingCards={item.floatingCards}
              />
            )}
          </div>

          <div className={`${imgLeft ? "lg:order-2" : "lg:order-1"} order-2`} style={copyStyle}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.12em] uppercase">
              <Icon className="w-3.5 h-3.5" />
              {item.label}
            </div>
            <h3
              className="mt-6 text-[44px] md:text-[64px] font-semibold text-slate-900 leading-[1.02] tracking-tight"
              data-testid={`pillar-${item.id}-title`}
            >
              {item.title}
              <span className="text-[#1d4ed8]">.</span>
            </h3>
            {item.secondaryHeading && (
              <p
                className="mt-3 text-[20px] md:text-[24px] font-medium text-slate-700 leading-snug"
                data-testid={`pillar-${item.id}-secondary`}
              >
                {item.secondaryHeading.split("Maya").map((chunk, i, arr) =>
                  i === arr.length - 1 ? (
                    <span key={i}>{chunk}</span>
                  ) : (
                    <React.Fragment key={i}>
                      {chunk}
                      <span className="text-[#1d4ed8]">Maya</span>
                    </React.Fragment>
                  )
                )}
              </p>
            )}
            <p className="mt-5 text-slate-600 text-[17px] leading-relaxed max-w-[540px]">
              {item.description}
            </p>
            {item.bullets && item.bullets.length > 0 && (
              <ul className="mt-7 space-y-3 max-w-[520px]" data-testid={`pillar-${item.id}-bullets`}>
                {item.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 w-6 h-6 shrink-0 rounded-full bg-[#1d4ed8]/10 text-[#1d4ed8] flex items-center justify-center">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-[15.5px] text-slate-700 leading-relaxed">{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {item.postBulletsNote && (
              <p
                className="mt-5 text-slate-600 text-[15.5px] leading-relaxed max-w-[540px]"
                data-testid={`pillar-${item.id}-post-note`}
              >
                {item.postBulletsNote}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const PillarsSection = () => (
  <div data-testid="pillars-container">
    {PILLARS.map((p) => (
      <Pillar key={p.id} item={p} />
    ))}
  </div>
);

/* ==================================================================
   PASTEL IMAGE GALLERY  (assets pending)
   ================================================================== */

const GALLERY_LEFT = "/homepage/gallery-left.png";
const GALLERY_RIGHT = "/homepage/gallery-right.png";

const GalleryImage = ({ src, alt, title, subtitle, className = "", testid }) => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(0, 24px, 0) scale(0.98)",
    opacity: inView ? 1 : 0,
    transition: "transform 1000ms cubic-bezier(0.22, 1, 0.36, 1), opacity 800ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <div
      ref={ref}
      className={`group relative rounded-3xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(15,23,42,0.25)] hover:shadow-[0_35px_80px_-20px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all duration-500 ${className}`}
      style={style}
      data-testid={testid}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          draggable="false"
        />
      ) : (
        <div className="w-full h-full min-h-[300px] bg-gradient-to-br from-[#dbe6ff] via-[#eef4ff] to-[#f6f9ff] flex items-center justify-center">
          <span className="text-[12px] uppercase tracking-wider text-[#1d4ed8]/60 font-medium">Awaiting artwork</span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
        {subtitle && <p className="text-[12px] uppercase tracking-[0.14em] opacity-90">{subtitle}</p>}
        <p className="mt-1 text-[22px] md:text-[26px] font-semibold leading-tight max-w-[420px]">{title}</p>
      </div>
    </div>
  );
};

const PastelGallery = () => (
  <section className="max-w-7xl mx-auto px-6 pt-24 pb-20" data-testid="pastel-gallery-section">
    <div className="grid md:grid-cols-3 gap-5">
      <GalleryImage
        src={GALLERY_LEFT}
        alt="Master what matters"
        subtitle="Master what matters"
        title="Forget information overload. Focus on the concepts that actually shape top-scoring MMI answers."
        className="md:col-span-2 min-h-[380px]"
        testid="gallery-left"
      />
      <GalleryImage
        src={GALLERY_RIGHT}
        alt="Made by medics, for future medics."
        title="Made by medics, for future medics."
        className="min-h-[380px]"
        testid="gallery-right"
      />
    </div>
  </section>
);

/* ==================================================================
   COMPLETE MMI PREPARATION GUIDE (book asset pending)
   ================================================================== */

const BOOK_IMAGE = "/homepage/book.png";

const MMIGuide = () => {
  const { ref, inView } = useReveal();
  const textStyle = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(-24px, 0, 0)",
    opacity: inView ? 1 : 0,
    transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  const bookStyle = {
    transform: inView ? "translate3d(0,0,0) scale(1)" : "translate3d(30px, 0, 0) scale(0.94)",
    opacity: inView ? 1 : 0,
    transition: "transform 1100ms cubic-bezier(0.22, 1, 0.36, 1) 160ms, opacity 900ms cubic-bezier(0.22, 1, 0.36, 1) 160ms",
    willChange: "transform, opacity",
  };
  return (
    <section ref={ref} className="max-w-7xl mx-auto px-6 pb-24" data-testid="mmi-guide-section">
      <div className="rounded-[32px] bg-gradient-to-br from-[#eef4ff] via-white to-[#f7faff] border border-white p-8 md:p-14 grid md:grid-cols-[1.15fr_1fr] gap-10 items-center relative overflow-hidden shadow-[0_45px_110px_-30px_rgba(29,78,216,0.55)]">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-[#dbe6ff]/60 rounded-full blur-3xl" />
        <div style={textStyle} className="relative">
          <span className="inline-block px-3 py-1 rounded-full bg-white text-[#1d4ed8] text-[12px] font-medium border border-[#dbe6ff]">
            Complete MMI Preparation Guide
          </span>
          <h3 className="mt-4 text-[32px] md:text-[44px] font-semibold text-slate-900 tracking-tight leading-[1.08]">
            Resources that{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#1d4ed8]">tell you what matters</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
            </span>{" "}
            — and leave out what doesn’t.
          </h3>
          <p className="mt-5 text-slate-600 max-w-[520px] leading-relaxed text-[16px]">
            Our guides are written to help you understand the key concepts most likely to shape your
            MMI answers, without burying you in unnecessary detail.
          </p>
        </div>
        <div className="relative flex justify-center" style={bookStyle}>
          <div className="absolute inset-0 -z-10 rounded-full blur-3xl bg-[#c9dcff] opacity-70 scale-90" />
          {BOOK_IMAGE ? (
            <img
              src={BOOK_IMAGE}
              alt="MMI Preparation Guide"
              className="medily-book-float w-[280px] md:w-[340px] h-auto drop-shadow-[0_45px_80px_rgba(29,78,216,0.35)]"
              data-testid="mmi-guide-book-img"
              draggable="false"
            />
          ) : (
            <div
              className="medily-book-float w-[240px] h-[340px] rounded-[14px] bg-gradient-to-br from-white to-[#eef4ff] shadow-[0_35px_60px_-15px_rgba(29,78,216,0.45)] border border-slate-100 flex flex-col p-6"
              data-testid="mmi-guide-book-placeholder"
            >
              <div className="text-[12px] font-semibold text-[#1d4ed8]">MEDILY</div>
              <div className="mt-6 text-[24px] font-semibold text-slate-900 leading-tight">
                MMI<br />Interview<br />Guide
              </div>
              <div className="mt-auto text-[10px] text-slate-400">medily.co.uk</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/* ==================================================================
   UNIVERSITY CAROUSEL (continuous infinite loop)
   ================================================================== */

const UNI_LOGOS = [
  { name: "Imperial College London", src: "/homepage/uni/imperial.png" },
  { name: "UCL", src: "/homepage/uni/ucl.png" },
  { name: "King's College London", src: "/homepage/uni/kings.png" },
  { name: "Queen Mary University of London", src: "/homepage/uni/queen-mary.png" },
  { name: "City St George's, University of London", src: "/homepage/uni/city.png" },
  { name: "University of Birmingham", src: "/homepage/uni/birmingham.png" },
  { name: "Cardiff University", src: "/homepage/uni/cardiff.png" },
  { name: "The University of Manchester", src: "/homepage/uni/manchester.png" },
  { name: "University of Bristol", src: "/homepage/uni/bristol.png" },
  { name: "The University of Edinburgh", src: "/homepage/uni/edinburgh.png" },
  { name: "University of Glasgow", src: "/homepage/uni/glasgow.png" },
  { name: "University of Leicester", src: "/homepage/uni/leicester.png" },
];

const UniversityCarousel = () => {
  // Duplicate for a seamless -50% marquee loop
  const items = [...UNI_LOGOS, ...UNI_LOGOS];
  return (
    <section className="pt-8 pb-20" data-testid="university-carousel-section">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-[26px] md:text-[34px] font-semibold text-slate-900 tracking-tight">
          Trusted by Aspiring Medics Targeting Top Universities
        </h2>
      </div>
      <div className="mt-10 relative medily-marquee-wrap">
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-white to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-white to-transparent z-10" />
        <div className="overflow-hidden">
          <div className="medily-marquee-track flex gap-5 w-max" data-testid="university-carousel-track">
            {items.map((u, i) => (
              <div
                key={i}
                className="w-[200px] h-[110px] shrink-0 rounded-2xl border border-slate-100 bg-white flex items-center justify-center px-5 py-4 shadow-[0_20px_45px_-20px_rgba(29,78,216,0.35)]"
                data-testid={`uni-card-${i}`}
                title={u.name}
              >
                <img
                  src={u.src}
                  alt={u.name}
                  className="max-w-full max-h-full w-auto h-auto object-contain select-none"
                  draggable="false"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-6 mt-10">
        <p className="text-[12.5px] text-slate-500 text-center leading-relaxed">
          Interview formats and assessment styles may change. Medily is an independent preparation
          platform and is not affiliated with any university. Medily cannot guarantee the exact
          questions or interview format you will encounter.
        </p>
      </div>
    </section>
  );
};

/* ==================================================================
   TESTIMONIALS
   ================================================================== */

const TESTIMONIALS = [
  {
    body: "The best part wasn’t just getting feedback — it was seeing how Maya would improve my answer afterwards. It completely changed how I approached every station.",
    name: "Olivia R.",
    school: "Offer: University College London",
  },
  {
    body: "Practising university-specific stations made a huge difference. My Imperial interview felt incredibly familiar because I’d already experienced the style with Maya.",
    name: "Daniel M.",
    school: "Offer: Imperial College London",
  },
  {
    body: "The guides explain exactly what you need to know without overwhelming you. I stopped wasting time on random notes and finally felt confident going into my interviews.",
    name: "Aisha K.",
    school: "Offer: King’s College London",
  },
];

const TestimonialCard = ({ t, i }) => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 24px, 0)",
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms`,
    willChange: "transform, opacity",
  };
  return (
    <div
      ref={ref}
      className="rounded-3xl bg-white border border-slate-100 p-7 shadow-[0_25px_55px_-20px_rgba(29,78,216,0.3)] hover:shadow-[0_35px_75px_-20px_rgba(29,78,216,0.4)] hover:-translate-y-1 transition-all duration-500"
      style={style}
      data-testid={`testimonial-card-${i}`}
    >
      <Quote className="w-7 h-7 text-[#1d4ed8]/40" />
      <p className="mt-4 text-slate-700 leading-relaxed text-[15.5px]">“{t.body}”</p>
      <div className="mt-6 pt-5 border-t border-slate-100">
        <p className="font-semibold text-slate-900 text-[14.5px]">{t.name}</p>
        <p className="text-[13px] text-[#1d4ed8] mt-0.5">{t.school}</p>
      </div>
    </div>
  );
};

const Testimonials = () => (
  <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="testimonials-section">
    <div className="text-center max-w-2xl mx-auto">
      <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">
        Success Stories
      </span>
      <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight">
        Loved by future doctors
      </h2>
      <p className="mt-3 text-slate-500">Real preparation. Genuine confidence. Better interviews.</p>
    </div>
    <div className="mt-12 grid md:grid-cols-3 gap-6">
      {TESTIMONIALS.map((t, i) => (
        <TestimonialCard key={i} t={t} i={i} />
      ))}
    </div>
  </section>
);

/* ==================================================================
   FAQ
   ================================================================== */

const FAQS = [
  {
    q: "What makes Maya different from other AI interview tools?",
    a: "Maya doesn’t just tell you whether your answer was good or bad. She scores your performance against real MMI marking criteria, provides university-specific feedback, and even rewrites your answer to show you what a top-mark response could look like. It’s like practising with an interviewer and a tutor at the same time.",
  },
  {
    q: "Are the resources suitable if I’m just starting my MMI preparation?",
    a: "Absolutely. Whether you’re just starting your preparation or refining your interview technique, Medily guides you from the fundamentals through to realistic MMI practice with expert-written resources, structured learning and detailed feedback.",
  },
  {
    q: "Is there a limit to how many MMI stations I can complete?",
    a: "Yes. Your Resources subscription includes 15 Maya MMI stations every month. Your allowance resets automatically each monthly billing cycle.",
  },
  {
    q: "How long does it take to complete the resources?",
    a: "Most topics take between 1 and 3 minutes to read, making it easy to fit preparation around school or college. You can complete topics in any order and revisit them whenever you need a refresher.",
  },
  {
    q: "How quickly do I get access to the guides and study resources?",
    a: "Immediately. As soon as your payment is confirmed, you’ll have instant access to all of your guides, study resources and your Maya dashboard.",
  },
];

const FaqItem = ({ f, i, open, setOpen }) => {
  const isOpen = open === i;
  const contentRef = useRef(null);
  const [maxH, setMaxH] = useState(0);
  useEffect(() => {
    if (!contentRef.current) return;
    setMaxH(isOpen ? contentRef.current.scrollHeight : 0);
  }, [isOpen]);
  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-[0_10px_25px_-15px_rgba(15,23,42,0.2)]"
      data-testid={`faq-item-${i}`}
    >
      <button
        onClick={() => setOpen(isOpen ? -1 : i)}
        className="w-full flex items-center justify-between px-6 py-5 text-left"
        data-testid={`faq-toggle-${i}`}
      >
        <span className="font-medium text-slate-900 text-[15.5px] pr-4">{f.q}</span>
        <span className="w-8 h-8 rounded-full bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center shrink-0 transition-transform duration-300" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
          {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>
      <div
        style={{
          maxHeight: `${maxH}px`,
          transition: "max-height 260ms ease-out",
          overflow: "hidden",
        }}
      >
        <div ref={contentRef} className="px-6 pb-5 text-slate-600 leading-relaxed text-[14.5px] -mt-1">
          {f.a}
        </div>
      </div>
    </div>
  );
};

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="max-w-4xl mx-auto px-6 pb-24" data-testid="faq-section">
      <div className="text-center max-w-2xl mx-auto">
        <span className="inline-block px-3 py-1 rounded-full bg-[#eef4ff] text-[#1d4ed8] text-[12px] font-medium">
          FAQ
        </span>
        <h2 className="mt-4 text-[34px] md:text-[46px] font-semibold text-slate-900 tracking-tight">
          Frequently asked questions
        </h2>
      </div>
      <div className="mt-10 space-y-3">
        {FAQS.map((f, i) => (
          <FaqItem key={i} f={f} i={i} open={open} setOpen={setOpen} />
        ))}
      </div>
    </section>
  );
};

/* ==================================================================
   FINAL CTA
   ================================================================== */

const FinalCta = () => {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="final-cta-section">
      <div className="rounded-[32px] bg-gradient-to-br from-[#eef4ff] via-white to-[#dbe6ff] border border-white p-12 md:p-20 text-center shadow-[0_45px_110px_-30px_rgba(29,78,216,0.55)]">
        <h3 className="text-[34px] md:text-[52px] font-semibold text-slate-900 tracking-tight leading-[1.05]">
          Everything you need.{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#1d4ed8]">One place</span>
            <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>{" "}
          to prepare.
        </h3>
        <p className="mt-5 text-slate-600 max-w-2xl mx-auto text-[16.5px] leading-relaxed">
          From expert-written guides to AI-powered practice and realistic mock interviews, Medily
          gives you everything you need to walk into your MMI with confidence.
        </p>
        <div className="mt-9 flex justify-center">
          <button
            onClick={() => navigate("/pricing")}
            className="px-8 py-4 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_45px_-12px_rgba(29,78,216,0.6)] inline-flex items-center gap-2"
            data-testid="final-cta-btn"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ==================================================================
   PAGE
   ================================================================== */

const HomePage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <Hero />
      <StatsBand />
      <WhyChooseUs />
      <PillarsSection />
      <PastelGallery />
      <MMIGuide />
      <UniversityCarousel />
      <Testimonials />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
};

export default HomePage;
